/**
 * TemperatureSetting Style03 - Style05 base + ambient temperature state row.
 * Keep +/- and circular setpoint controls while exposing measured temperature.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import type { TraitControlProps } from './types'
import { resolveRangeFromAttributes } from './traitStyleConfig'
import './TemperatureSettingStyle03.css'

const HOLD_DELAY_MS = 400
const REPEAT_INTERVAL_MS = 150
const TOUCH_CLICK_SUPPRESS_MS = 700

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.trim())
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

export function TemperatureSettingStyle03({
  trait,
  state,
  attributes,
  onCommand,
  disabled = false,
}: TraitControlProps) {
  const { min: MIN, max: MAX } = useMemo(
    () => resolveRangeFromAttributes(attributes, trait, 'temperature'),
    [attributes, trait]
  )
  const rawSetpoint = toFiniteNumber(
    state?.thermostatTemperatureSetpoint
    ?? state?.temperatureSetpoint
    ?? state?.targetTemperature
    ?? state?.setpoint
    ?? state?.tempSetpoint
    ?? state?.temperature
    ?? state?.temp
  )
  const current = rawSetpoint != null
    ? Math.max(MIN, Math.min(MAX, Math.round(rawSetpoint)))
    : Math.round((MIN + MAX) / 2)
  const [temp, setTemp] = useState(current)

  const directAmbient = toFiniteNumber(
    state?.thermostatTemperatureAmbient
    ?? state?.temperatureAmbient
    ?? state?.ambientTemperature
    ?? state?.currentTemperature
    ?? state?.temperatureMeasurement
    ?? state?.measuredTemperature
    ?? state?.sensorTemperature
  )
  const fallbackAmbient = rawSetpoint != null
    ? toFiniteNumber(state?.temperature ?? state?.temp)
    : null
  const ambient = directAmbient ?? fallbackAmbient
  const ambientText = ambient == null ? '--' : `${ambient.toFixed(1)}°C`

  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const repeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const tempRef = useRef(temp)
  const skipNextClickRef = useRef(false)
  const resetSkipRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastTouchAtRef = useRef(0)

  useEffect(() => {
    tempRef.current = temp
  }, [temp])

  useEffect(() => {
    const next = rawSetpoint != null
      ? Math.max(MIN, Math.min(MAX, Math.round(rawSetpoint)))
      : Math.round((MIN + MAX) / 2)
    setTemp(next)
  }, [rawSetpoint, MIN, MAX])

  useEffect(() => () => {
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current)
    if (repeatTimerRef.current) clearInterval(repeatTimerRef.current)
    if (resetSkipRef.current) clearTimeout(resetSkipRef.current)
  }, [])

  const stopHold = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current)
      holdTimerRef.current = null
    }
    if (repeatTimerRef.current) {
      clearInterval(repeatTimerRef.current)
      repeatTimerRef.current = null
    }
    if (resetSkipRef.current) {
      clearTimeout(resetSkipRef.current)
      resetSkipRef.current = null
    }
    resetSkipRef.current = setTimeout(() => {
      skipNextClickRef.current = false
      resetSkipRef.current = null
    }, 100)
  }, [])

  const handleDecrement = useCallback(() => {
    if (disabled) return
    const next = Math.max(MIN, tempRef.current - 1)
    setTemp(next)
    void onCommand('setTemperature', { temperature: next })
  }, [disabled, MIN, onCommand])

  const handleIncrement = useCallback(() => {
    if (disabled) return
    const next = Math.min(MAX, tempRef.current + 1)
    setTemp(next)
    void onCommand('setTemperature', { temperature: next })
  }, [disabled, MAX, onCommand])

  const startHoldDecrement = () => {
    if (disabled || temp <= MIN) return
    skipNextClickRef.current = true
    handleDecrement()
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null
      repeatTimerRef.current = setInterval(handleDecrement, REPEAT_INTERVAL_MS)
    }, HOLD_DELAY_MS)
  }

  const startHoldIncrement = () => {
    if (disabled || temp >= MAX) return
    skipNextClickRef.current = true
    handleIncrement()
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null
      repeatTimerRef.current = setInterval(handleIncrement, REPEAT_INTERVAL_MS)
    }, HOLD_DELAY_MS)
  }

  return (
    <div className="trait-temperaturesetting-style03 trait-stitch-glass">
      <div className="trait-temperaturesetting-style03__header">
        <span
          className="trait-temperaturesetting-style03__title"
          data-css-slot="temperatureTitle"
          data-css-slot-label="Temperature Title"
        >
          Temperature
        </span>
        <span
          className="material-symbols-outlined trait-temperaturesetting-style03__icon"
          data-css-slot="temperatureHeaderIcon"
          data-css-slot-label="Temperature Header Icon"
        >
          thermostat
        </span>
      </div>

      <div
        className="trait-temperaturesetting-style03__state"
        data-css-slot="temperatureStateRow"
        data-css-slot-label="Temperature State Row"
      >
        <span
          className="trait-temperaturesetting-style03__state-label"
          data-css-slot="temperatureStateLabel"
          data-css-slot-label="Temperature State Label"
        >
          Sensor
        </span>
        <span
          className="trait-temperaturesetting-style03__state-value"
          data-css-slot="temperatureStateValue"
          data-css-slot-label="Temperature State Value"
        >
          {ambientText}
        </span>
      </div>

      <div className="trait-temperaturesetting-style03__controls">
        <button
          type="button"
          className="trait-temperaturesetting-style03__btn trait-temperaturesetting-style03__btn-minus"
          data-css-slot="minusBtn"
          data-css-slot-label="Minus Button"
          onClick={() => {
            if (Date.now() - lastTouchAtRef.current < TOUCH_CLICK_SUPPRESS_MS) return
            if (skipNextClickRef.current) {
              skipNextClickRef.current = false
              return
            }
            handleDecrement()
          }}
          onMouseDown={startHoldDecrement}
          onMouseUp={stopHold}
          onMouseLeave={stopHold}
          onTouchStart={(e) => {
            e.preventDefault()
            lastTouchAtRef.current = Date.now()
            startHoldDecrement()
          }}
          onTouchEnd={(e) => {
            e.preventDefault()
            stopHold()
          }}
          onTouchCancel={(e) => {
            e.preventDefault()
            stopHold()
          }}
          disabled={disabled || temp <= MIN}
        >
          <span
            className="material-symbols-outlined"
            data-css-slot="minusBtnIcon"
            data-css-slot-label="Minus Button Icon"
          >
            remove
          </span>
        </button>

        <div
          className="trait-temperaturesetting-style03__circle"
          data-css-slot="temperatureCircle"
          data-css-slot-label="Temperature Circle"
        >
          <div
            className="trait-temperaturesetting-style03__arc"
            data-css-slot="temperatureArc"
            data-css-slot-label="Temperature Arc"
            style={{ '--progress': MAX > MIN ? (temp - MIN) / (MAX - MIN) : 0.5 } as React.CSSProperties}
          />
          <span
            className="trait-temperaturesetting-style03__value"
            data-css-slot="temperatureValue"
            data-css-slot-label="Temperature Value"
          >
            {temp}
            <span
              className="trait-temperaturesetting-style03__unit"
              data-css-slot="temperatureUnit"
              data-css-slot-label="Temperature Unit"
            >
              °C
            </span>
          </span>
        </div>

        <button
          type="button"
          className="trait-temperaturesetting-style03__btn trait-temperaturesetting-style03__btn-plus"
          data-css-slot="plusBtn"
          data-css-slot-label="Plus Button"
          onClick={() => {
            if (Date.now() - lastTouchAtRef.current < TOUCH_CLICK_SUPPRESS_MS) return
            if (skipNextClickRef.current) {
              skipNextClickRef.current = false
              return
            }
            handleIncrement()
          }}
          onMouseDown={startHoldIncrement}
          onMouseUp={stopHold}
          onMouseLeave={stopHold}
          onTouchStart={(e) => {
            e.preventDefault()
            lastTouchAtRef.current = Date.now()
            startHoldIncrement()
          }}
          onTouchEnd={(e) => {
            e.preventDefault()
            stopHold()
          }}
          onTouchCancel={(e) => {
            e.preventDefault()
            stopHold()
          }}
          disabled={disabled || temp >= MAX}
        >
          <span
            className="material-symbols-outlined"
            data-css-slot="plusBtnIcon"
            data-css-slot-label="Plus Button Icon"
          >
            add
          </span>
        </button>
      </div>
    </div>
  )
}
